import React, { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Download, Smartphone } from 'lucide-react';
import { getCurrentLogUri, listLogFiles, readCurrentLog } from '@/lib/fileLogger';
import { Btn, CopyButton, Notice, Page, Panel, RefreshButton } from './ui';

/* The log file the app keeps on this device (also at /debug/logs). Useful
   when the console is opened inside the installed app. */
export default function LogsPanel() {
  const platform = Capacitor.getPlatform();
  const isNative = platform === 'ios' || platform === 'android';
  const [log, setLog] = useState({ text: '', uri: '', files: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [text, uri, files] = await Promise.all([readCurrentLog(), getCurrentLogUri(), listLogFiles()]);
      setLog({ text: text || '', uri: uri || '', files: files || [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const download = () => {
    const url = URL.createObjectURL(new Blob([log.text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-${new Date().toISOString().slice(0, 10)}.log`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Page
      title="Device logs"
      description="The log the app writes on this device, for diagnosing problems without a cable."
      actions={(
        <>
          <CopyButton text={log.text} label="Copy" className="h-8 border border-border px-3" />
          <Btn variant="outline" size="sm" onClick={download} disabled={!log.text}>
            <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Download</span>
          </Btn>
          <RefreshButton onClick={refresh} loading={loading} />
        </>
      )}
    >
      {!isNative && (
        <Notice tone="neutral" icon={Smartphone} title="You are in a browser">
          In a browser this shows only this tab’s in-memory log. Open the admin console inside the installed app to read the log saved on the phone.
        </Notice>
      )}
      <Panel
        flush
        title={isNative ? 'Current log file' : 'In-memory log'}
        description={isNative ? (log.uri || 'Locating the log file…') : `Platform: ${platform}`}
      >
        <pre className="max-h-[60vh] min-h-[320px] overflow-auto whitespace-pre bg-zinc-950 p-4 font-mono text-[11px] leading-relaxed text-zinc-200">
          {log.text || (loading ? 'Loading…' : 'No log entries yet. Use the app, then refresh.')}
        </pre>
      </Panel>
      {log.files.length > 0 && (
        <Panel title="Files on this device" description={`${log.files.length} in the logs folder`}>
          <ul className="space-y-1.5 text-xs">
            {log.files.map((f) => (
              <li key={f.name} className="flex flex-wrap justify-between gap-2">
                <code className="text-foreground">{f.name}</code>
                <span className="text-muted-foreground">{f.size != null ? `${f.size.toLocaleString('en-US')} bytes` : ''}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </Page>
  );
}
